<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <!-- Document Requirements -->
        <div class="card card-top-line">
          <div class="header d-flex align-items-center justify-content-between">
            <h2>Document Requirements</h2>
            <b-button variant="primary btn-mw" size="lg" @click="openCreateModal">
              Add Requirement <i class="fa fa-plus-square ml-1"></i>
            </b-button>
          </div>
          <div class="body pt-0">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-3">
                <div class="form-group mb-0">
                  <select class="form-control" v-model="filterCountryId" @change="loadRequirements">
                    <option value="">All Countries</option>
                    <option
                      v-for="c in countries"
                      :key="c.id"
                      :value="c.id"
                    >
                      {{ c.name }} ({{ c.code }})
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group mb-0">
                  <select class="form-control" v-model="filterBusinessTypeId" @change="loadRequirements">
                    <option value="">All Business Types</option>
                    <option
                      v-for="bt in businessTypes"
                      :key="bt.id"
                      :value="bt.id"
                    >
                      {{ bt.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group mb-0">
                  <select class="form-control" v-model="filterCategory" @change="loadRequirements">
                    <option value="">All Categories</option>
                    <option value="business">Business</option>
                    <option value="identity">Identity</option>
                    <option value="address">Address</option>
                    <option value="financial">Financial</option>
                    <option value="regulatory">Regulatory</option>
                    <option value="edd">EDD</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group mb-0">
                  <select class="form-control" v-model="filterActive" @change="loadRequirements">
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Requirements Table -->
            <b-table
              responsive
              hover
              outlined
              table-class="js-basic-example table-custom mb-0"
              head-variant="light"
              :items="documentRequirements"
              :fields="requirementFields"
              :busy="isLoading"
              show-empty
              :emptyText="'No document requirements found.'"
              striped
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-2">Loading...</p>
                </div>
              </template>

              <template #cell(document_type)="{ item }">
                <div>
                  <strong>{{ item.document_type.name }}</strong>
                  <br />
                  <small class="text-muted">{{ item.document_type.code }}</small>
                </div>
              </template>

              <template #cell(category)="{ item }">
                <span
                  class="badge"
                  :class="categoryBadgeClass(item.document_type.category)"
                >
                  {{ item.document_type.category }}
                </span>
              </template>

              <template #cell(country)="{ item }">
                {{ item.country ? item.country.name + ' (' + item.country.code + ')' : 'All Countries' }}
              </template>

              <template #cell(business_type)="{ item }">
                {{ item.business_type ? item.business_type.name : 'All Types' }}
              </template>

              <template #cell(flags)="{ item }">
                <span v-if="item.is_mandatory" class="badge badge-primary mr-1">Mandatory</span>
                <span v-if="item.is_high_risk_only" class="badge badge-warning mr-1">High Risk</span>
                <span v-if="item.is_regulated_only" class="badge badge-info mr-1">Regulated</span>
                <span v-if="item.is_edd" class="badge badge-dark mr-1">EDD</span>
                <span v-if="item.stakeholder_type" class="badge badge-secondary mr-1">
                  {{ item.stakeholder_type }}
                </span>
                <span v-if="item.ownership_threshold" class="badge badge-light">
                  &ge; {{ item.ownership_threshold }}%
                </span>
              </template>

              <template #cell(is_active)="{ item }">
                <span
                  class="badge"
                  :class="item.is_active ? 'badge-success' : 'badge-danger'"
                >
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </template>

              <template #cell(actions)="{ item }">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1"
                  @click="openEditModal(item)"
                  title="Edit"
                >
                  <i class="fa fa-pencil"></i>
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="confirmDelete(item)"
                  title="Delete"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <b-modal
      id="requirement-modal"
      :title="editingRequirement ? 'Edit Document Requirement' : 'Add Document Requirement'"
      hide-footer
      size="lg"
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <div class="modal-area">
        <ValidationObserver v-slot="{ passes }">
          <form @submit.prevent="passes(saveRequirement)">
            <div class="row">
              <!-- Document Type -->
              <div class="col-md-6">
                <ValidationProvider name="document_type" rules="required" v-slot="{ errors }">
                  <div class="form-group" :class="{ errors: errors[0], success: form.document_type_id && !errors[0] }">
                    <label>Document Type *</label>
                    <select
                      class="form-control"
                      v-model="form.document_type_id"
                      @change="onDocTypeChange"
                      :disabled="!!editingRequirement"
                    >
                      <option value="">Select document type</option>
                      <option
                        v-for="dt in documentTypes"
                        :key="dt.id"
                        :value="dt.id"
                      >
                        {{ dt.name }} ({{ dt.category }})
                      </option>
                    </select>
                    <span class="text-danger error-msg" v-if="errors[0]">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>

              <!-- Rule Name -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Rule Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.rule_name"
                    placeholder="e.g. Ghana LLC incorporation"
                  />
                </div>
              </div>

              <!-- Country -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Country</label>
                  <select class="form-control" v-model="form.country_id">
                    <option value="">All Countries</option>
                    <option
                      v-for="c in countries"
                      :key="c.id"
                      :value="c.id"
                    >
                      {{ c.name }} ({{ c.code }})
                    </option>
                  </select>
                </div>
              </div>

              <!-- Business Type -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Business Type</label>
                  <select class="form-control" v-model="form.business_type_id">
                    <option value="">All Business Types</option>
                    <option
                      v-for="bt in businessTypes"
                      :key="bt.id"
                      :value="bt.id"
                    >
                      {{ bt.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Stakeholder Type -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Stakeholder Type</label>
                  <select class="form-control" v-model="form.stakeholder_type">
                    <option :value="null">None</option>
                    <option value="director">Director</option>
                    <option value="shareholder">Shareholder</option>
                    <option value="ubo">UBO</option>
                  </select>
                </div>
              </div>

              <!-- Ownership Threshold -->
              <div class="col-md-6">
                <div class="form-group">
                  <label>Ownership Threshold (%)</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="form.ownership_threshold"
                    min="0"
                    max="100"
                    placeholder="e.g. 25"
                  />
                  <small class="text-muted">Required when ownership exceeds this %</small>
                </div>
              </div>

              <!-- Flags -->
              <div class="col-md-6">
                <div class="form-group">
                  <label class="d-block">Flags</label>
                  <div class="d-flex flex-wrap">
                    <b-form-checkbox v-model="form.is_mandatory" class="mr-3">Mandatory</b-form-checkbox>
                    <b-form-checkbox v-model="form.is_high_risk_only" class="mr-3">High Risk Only</b-form-checkbox>
                    <b-form-checkbox v-model="form.is_regulated_only" class="mr-3">Regulated Only</b-form-checkbox>
                    <b-form-checkbox v-model="form.is_edd" class="mr-3">EDD</b-form-checkbox>
                    <b-form-checkbox v-model="form.is_active">Active</b-form-checkbox>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end mt-3">
              <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('requirement-modal')">
                Cancel
              </b-button>
              <b-button variant="primary" type="submit" :disabled="saving">
                <b-spinner small v-if="saving" class="mr-1"></b-spinner>
                {{ editingRequirement ? 'Update' : 'Create' }}
              </b-button>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </b-modal>

    <main-loader ref="loader"></main-loader>
  </div>
</template>

<script>
import shared from "@/shared.js";
import { mapState } from "vuex";
import "@/plugins/vee-validate";

const { ValidationObserver, ValidationProvider } = require("vee-validate");
const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;

export default {
  name: "DocumentRequirementsConfig",
  components: {
    ValidationObserver,
    ValidationProvider,
    "main-loader": LoaderComponent,
  },
  data() {
    return {
      // Filters
      filterCountryId: "",
      filterBusinessTypeId: "",
      filterCategory: "",
      filterActive: "",

      // Requirements table
      requirementFields: [
        { key: "document_type", label: "Document Type", sortable: true },
        { key: "category", label: "Category" },
        { key: "country", label: "Country" },
        { key: "business_type", label: "Business Type" },
        { key: "flags", label: "Flags" },
        { key: "is_active", label: "Status" },
        { key: "actions", label: "Actions" },
      ],

      // Form
      editingRequirement: null,
      saving: false,
      form: this.getEmptyForm(),
    };
  },
  computed: {
    ...mapState("config", [
      "documentRequirements",
      "documentTypes",
      "businessTypes",
      "countries",
      "isLoading",
    ]),
  },
  mounted() {
    this.loadLookups();
    this.loadRequirements();
  },
  methods: {
    getEmptyForm() {
      return {
        document_type_id: "",
        document_type_code: "",
        rule_name: "",
        country_id: "",
        business_type_id: "",
        stakeholder_type: null,
        ownership_threshold: null,
        is_mandatory: true,
        is_high_risk_only: false,
        is_regulated_only: false,
        is_edd: false,
        is_active: true,
      };
    },

    async loadLookups() {
      this.$refs.loader.show();
      try {
        await Promise.all([
          this.$store.dispatch("config/fetchDocumentTypes"),
          this.$store.dispatch("config/fetchBusinessTypes"),
          this.$store.dispatch("config/fetchCountries"),
        ]);
      } catch (e) {
        shared.toastrError("Failed to load configuration data");
      } finally {
        this.$refs.loader.hide();
      }
    },

    async loadRequirements() {
      const params = {};
      if (this.filterCountryId) params.country_id = this.filterCountryId;
      if (this.filterBusinessTypeId) params.business_type_id = this.filterBusinessTypeId;
      if (this.filterCategory) params.category = this.filterCategory;
      if (this.filterActive) params.is_active = this.filterActive;

      try {
        await this.$store.dispatch("config/fetchDocumentRequirements", params);
      } catch (e) {
        shared.toastrError("Failed to load document requirements");
      }
    },

    onDocTypeChange() {
      const dt = this.documentTypes.find((d) => d.id === this.form.document_type_id);
      if (dt) {
        this.form.document_type_code = dt.code;
      }
    },

    openCreateModal() {
      this.editingRequirement = null;
      this.form = this.getEmptyForm();
      this.$bvModal.show("requirement-modal");
    },

    openEditModal(item) {
      this.editingRequirement = item;
      this.form = {
        document_type_id: item.document_type.id,
        document_type_code: item.document_type_code,
        rule_name: item.rule_name || "",
        country_id: item.country ? item.country.id : "",
        business_type_id: item.business_type ? item.business_type.id : "",
        stakeholder_type: item.stakeholder_type || null,
        ownership_threshold: item.ownership_threshold,
        is_mandatory: item.is_mandatory,
        is_high_risk_only: item.is_high_risk_only,
        is_regulated_only: item.is_regulated_only,
        is_edd: item.is_edd,
        is_active: item.is_active,
      };
      this.$bvModal.show("requirement-modal");
    },

    async saveRequirement() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        // Clean empty optional fields
        if (!payload.country_id) delete payload.country_id;
        if (!payload.business_type_id) delete payload.business_type_id;
        if (!payload.rule_name) delete payload.rule_name;
        if (!payload.stakeholder_type) delete payload.stakeholder_type;
        if (payload.ownership_threshold === null || payload.ownership_threshold === "") {
          delete payload.ownership_threshold;
        }

        if (this.editingRequirement) {
          // Update - only send changed fields
          delete payload.document_type_id;
          delete payload.document_type_code;
          await this.$store.dispatch("config/updateDocumentRequirement", {
            id: this.editingRequirement.id,
            payload,
          });
          shared.toastrSuccess("Document requirement updated successfully");
        } else {
          await this.$store.dispatch("config/createDocumentRequirement", payload);
          shared.toastrSuccess("Document requirement created successfully");
        }

        this.$bvModal.hide("requirement-modal");
        this.loadRequirements();
      } catch (e) {
        const msg = e.response?.data?.message || "Failed to save document requirement";
        shared.toastrError(msg);
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(item) {
      this.$swal
        .fire({
          title: "Delete Requirement",
          text: `Are you sure you want to delete the requirement for "${item.document_type.name}"?`,
          icon: "warning",
          customClass: {
            confirmButton: "btn btn-danger btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
          showCancelButton: true,
          confirmButtonText: "Yes, Delete",
          cancelButtonText: "Cancel",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await this.$store.dispatch("config/deleteDocumentRequirement", item.id);
              shared.toastrSuccess("Document requirement deleted");
              this.loadRequirements();
            } catch (e) {
              shared.toastrError("Failed to delete document requirement");
            }
          }
        });
    },

    resetForm() {
      this.editingRequirement = null;
      this.form = this.getEmptyForm();
    },

    categoryBadgeClass(category) {
      const map = {
        business: "badge-primary",
        identity: "badge-success",
        address: "badge-info",
        financial: "badge-warning",
        regulatory: "badge-danger",
        edd: "badge-dark",
      };
      return map[category] || "badge-secondary";
    },
  },
};
</script>
